import React, { useCallback, useState } from 'react';
import {
    Drawer,
    Paper,
    Button,
    Grid,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogActions,
    GridSize
} from '@mui/material';
import { SupervisorAccount } from '@mui/icons-material';

import { memberProps, useAuth, useMembersContext } from '../../context';
import { Text, Title } from '../../styles';
import { alertActionTypes, useFetch } from '../../hooks';

interface ViewMembersTableDrawerProps {
    open: boolean;
    closeHandler: () => void;
    member: memberProps;
    dispatchAlert: React.Dispatch<alertActionTypes>;
}

export const ViewMembersTableDrawer: React.FC<ViewMembersTableDrawerProps> =
    React.memo(({ open, closeHandler, member, dispatchAlert }) => {
        const { session } = useAuth();
        const { setAdminMembers, setEmployeeMembers } = useMembersContext();
        const [fetchHandler] = useFetch('/dbChangeMemberRole', 'PUT');
        const [openDialog, setOpenDialog] = useState<boolean>(false);

        const openDialogHandler = useCallback(
            () => setOpenDialog(prevOpenDialog => !prevOpenDialog),
            []
        );

        const changeRole = React.useCallback(async () => {
            const result = await fetchHandler({
                data: {
                    member_guid: session.MemberGuid,
                    session_guid: session.SessionGuid,
                    data: {
                        Role: member.Role === 2 ? 1 : 2,
                        MemberGuid: member.Guid
                    }
                }
            });
            if (result.success) {
                dispatchAlert({
                    type: 'UPDATE_ALERT',
                    message: `${member.Name} is now an ${
                        member.Role === 2 ? 'Admin' : 'Employee'
                    }`,
                    severity: 'success'
                });
                setAdminMembers(prevMembers =>
                    member.Role === 2
                        ? [...prevMembers, member]
                        : prevMembers.filter(
                              prevMember => prevMember.Guid !== member.Guid
                          )
                );
                setEmployeeMembers(prevMembers =>
                    member.Role !== 2
                        ? [...prevMembers, member]
                        : prevMembers.filter(
                              prevMember => prevMember.Guid !== member.Guid
                          )
                );
            } else {
                dispatchAlert({
                    type: 'UPDATE_ALERT',
                    message: result.msg,
                    severity: 'error'
                });
            }
        }, [
            dispatchAlert,
            member,
            fetchHandler,
            session.MemberGuid,
            session.SessionGuid,
            setAdminMembers,
            setEmployeeMembers
        ]);

        return (
            <Drawer anchor='left' open={open} onClose={closeHandler}>
                <Paper
                    sx={{
                        width: '100vw',
                        maxWidth: '70rem',
                        padding: '2rem 5vw',
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column'
                        // placeContent: 'center'
                    }}
                >
                    <Grid container spacing={5}>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '2rem'
                            }}
                        >
                            <Title size='2rem'>
                                {member.Name} {member.Surname}
                            </Title>
                            <Tooltip
                                title={
                                    member.Role === 2
                                        ? 'Change to Admin'
                                        : 'Change to Employee'
                                }
                                arrow
                            >
                                <IconButton onClick={openDialogHandler}>
                                    <SupervisorAccount color='action' />
                                </IconButton>
                            </Tooltip>
                            <Dialog
                                open={openDialog}
                                onClose={openDialogHandler}
                                aria-labelledby='alert-dialog-title'
                                aria-describedby='alert-dialog-title'
                            >
                                <DialogTitle id='alert-dialog-title'>
                                    Are you sure you want to make {member.Name}{' '}
                                    {member.Surname} an{' '}
                                    {member.Role === 2 ? 'Admin' : 'Employee'}?
                                </DialogTitle>

                                <DialogActions>
                                    <Button
                                        onClick={openDialogHandler}
                                        color='secondary'
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={changeRole}
                                        color='primary'
                                        autoFocus
                                    >
                                        Submit
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Grid>
                        <MemberField
                            title='Name'
                            content={member.Name + ' ' + member.Surname}
                        />
                        <MemberField title='Email' content={member.Email} />
                        <MemberField title='Phone' content={member.Phone} />
                        <MemberField
                            title='Emergency Contact'
                            content={member.Contact}
                        />
                        <MemberField
                            title='Address'
                            content={member.Address}
                            md={8}
                        />

                        <Grid item sm={12}>
                            <Button
                                fullWidth
                                size='large'
                                type='button'
                                variant='contained'
                                color='primary'
                                onClick={closeHandler}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Drawer>
        );
    });

interface MemberFieldProps {
    md?: GridSize | undefined;
    title: string;
    content?: string | number;
}

const MemberField: React.FC<MemberFieldProps> = React.memo(
    ({ title, content, md }) => (
        <Grid item sm={12} md={md ? md : 4}>
            <Text size='0.8rem'>{title}:</Text>
            <Text size='1rem' margin='0.3rem 0'>
                {content}
            </Text>
        </Grid>
    )
);
